/**
 * Region-Edit im LiveEditor: einen Bereich des fertigen Bildes neu rendern lassen
 * (Server: firebase_aq/functions-upscaler/src/regionEdit.ts, Callable regionEditCall).
 *
 *  - vorhandene Region erneut rendern, optional mit Wunsch-Text (Karte → „Neu rendern")
 *  - gefundenen, aber wegen der Obergrenze nicht gerenderten Bereich nachholen (report.analysis.moreRegions)
 *  - eigenen Bereich markieren: Rechteck auf dem Artwork ziehen, Typ + Wunsch-Text, los
 *  - Verlauf: jeder Edit ist im Job vermerkt (edits[]), „Zurück" stellt den Stand davor wieder her
 *
 * Der Fortschritt kommt über den Eltern-Job (upscaleJob.regionEdit), das neue Endbild über den Task.
 * Erwartet vom Editor: upscaleJob, upscaleAnalysis, imageLayout, localCanvas, $refs.frameRef.
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../../fb";
import { EventBus, events } from "../../../event-bus";

const regionalFunctions = getFunctions(app, "europe-west1");

export const REGION_TYPES = [
  { value: "face", label: "Gesicht" },
  { value: "animal_face", label: "Tiergesicht" },
  { value: "plate", label: "Kennzeichen" },
  { value: "logo", label: "Logo" },
  { value: "text", label: "Text / Schriftzug" },
  { value: "detail", label: "Sonstiges Detail" },
];

export default {
  data() {
    return {
      regionEditBusy: false,
      /** Rechteck ziehen: an/aus, laufende Auswahl (Promille) */
      regionDrawMode: false,
      regionDraw: null,
      /** Formular für einen markierten Bereich (nach dem Ziehen) */
      customRegion: null,
      /** Freier Text, der gerade über die Einordnung (Feedback-Edit) läuft */
      smartEditBusy: false,
      smartEditPrompt: "",
      /** Letzte Antwort „nicht zuordenbar" — bietet den alten Voll-Edit als Rückfall an */
      smartEditUnclear: null,
      /** Region, deren Formular in der großen Ansicht direkt offen sein soll */
      regionEditFocusId: null,
    };
  },
  computed: {
    /** Stand des letzten Edits am Eltern-Job (Fehlerzeile, Legacy) */
    regionEditState() {
      return (this.upscaleJob && this.upscaleJob.regionEdit) || null;
    },
    /** Alle gerade laufenden Bereichs-Edits (mehrere parallel möglich; Map regionEditsActive am Job) */
    regionEditsActive() {
      const fresh = (s) => !!s && (s.state === "queued" || s.state === "running") && Date.now() - (s.at || 0) < 15 * 60 * 1000;
      const map = (this.upscaleJob && this.upscaleJob.regionEditsActive) || null;
      if (map && typeof map === "object") {
        return Object.entries(map)
          .filter(([, v]) => fresh(v))
          .map(([jobId, v]) => ({ ...v, jobId }));
      }
      // Alte Jobs ohne Map: das einzelne Feld
      const s = this.regionEditState;
      return fresh(s) ? [s] : [];
    },
    regionEditRunning() {
      return this.regionEditsActive.length > 0;
    },
    regionEditRunningCount() {
      return this.regionEditsActive.length;
    },
    /** IDs der Bereiche, die gerade gerendert werden */
    regionEditRunningIds() {
      return this.regionEditsActive.map((s) => s.regionId).filter(Boolean);
    },
    /** Stand des Feedback-Edits (freier Text → Farbe/Ton, Bereich oder Detail mit geschützten Regionen) */
    feedbackEditState() {
      return (this.upscaleJob && this.upscaleJob.feedbackEdit) || null;
    },
    feedbackEditRunning() {
      const s = this.feedbackEditState;
      return !!s && (s.state === "queued" || s.state === "running") && Date.now() - (s.at || 0) < 15 * 60 * 1000;
    },
    /** Ergebnis-Zeile nur für frische Edits (letzte Stunde), nicht dauerhaft an alten Tasks */
    feedbackEditRecent() {
      const s = this.feedbackEditState;
      return !!s && Date.now() - (s.at || 0) < 60 * 60 * 1000;
    },
    /** Lesbarer Pfad des letzten Feedback-Edits */
    feedbackEditLabel() {
      const s = this.feedbackEditState;
      if (!s) return "";
      const scope = { tone: "Farbe/Ton angepasst", region: `Bereich neu gerendert${s.label ? ` (${s.label})` : ""}`, outside: "Detail geändert, Gesichter geschützt", unclear: "Nicht zuordenbar" }[s.scope] || "";
      if (s.state === "done") return `${scope || "Fertig"}${s.aiEditName ? ` → ${s.aiEditName}` : ""}`;
      if (s.state === "noAction") return `Nicht zuordenbar${s.reason ? `: ${s.reason}` : ""}`;
      if (s.state === "failed") return `Fehlgeschlagen${s.error ? `: ${s.error}` : ""}`;
      return "";
    },
    /** Gefundene, aber nicht gerenderte Bereiche (Obergrenze) */
    upscaleMoreRegions() {
      return ((this.upscaleAnalysis && this.upscaleAnalysis.moreRegions) || []).map((r) => ({ ...r, box: r.boxResult }));
    },
    regionEdits() {
      return (this.upscaleJob && this.upscaleJob.edits) || [];
    },
    /** Index des Edits, dessen Stand gerade als Version gewählt ist (DEFAULT = neuester Edit); -1 = Stand vor allen Edits */
    selectedJobEditIndex() {
      const edits = this.regionEdits;
      if (!edits.length) return -1;
      const v = String(this.selectedVariant || "");
      if (v === "DEFAULT") return edits.length - 1;
      if (v.startsWith("JOB_EDIT_")) return Math.min(edits.length - 1, Number(v.slice(9)));
      return -1;
    },
    /**
     * Änderungen, die in der gewählten Version enthalten sind (kumulativ: Edit 2 enthält Edit 1) — Kacheln in der
     * KI-Änderung-Box: Ausschnitt der Region (aktive Version dieses Edits) oder das ganze Bild bei Ton/Ganzbild-Edits.
     */
    jobEditTiles() {
      const edits = this.regionEdits;
      const upto = this.selectedJobEditIndex;
      if (upto < 0) return [];
      const versions = (this.upscaleJob && this.upscaleJob.regionVersions) || {};
      return edits.slice(0, upto + 1).map((e, i) => {
        let thumb = null;
        if (e.regionId && versions[e.regionId]) {
          const v = (versions[e.regionId].versions || []).find((x) => x.jobId === e.jobId);
          thumb = (v && v.entry && v.entry.after) || null;
        }
        if (!thumb) thumb = (e.final && e.final.url) || null;
        const scopeLabel = e.kind === "feedback" ? ({ tone: "Farbe/Ton", outside: "Detail, Gesichter geschützt", region: "Bereich" }[e.scope] || e.label || "Änderung") : e.label || "Bereich";
        return {
          index: i,
          thumb,
          wholeImage: !e.regionId,
          title: `Edit ${i + 1}: ${scopeLabel}`,
          instruction: e.instruction || e.comment || "",
          score: e.scoreAfter != null ? e.scoreAfter : null,
          scoreBefore: e.scoreBefore != null ? e.scoreBefore : null,
          warning: e.warning || null,
          at: e.at || 0,
        };
      });
    },
    /** Gibt es neben dem aktuellen Stand noch den reinen Upscale (vor Detail-Pass) als eigene Grundlage? */
    hasPlainUpscale() {
      const st = (this.upscaleJob && this.upscaleJob.stages) || {};
      return !!(st.upscale && st.upscale.url && st.final && st.final.url && st.upscale.url !== st.final.url);
    },
    /** Gibt es ein Kundenfoto, das als Vorlage mitgeschickt werden kann? */
    hasCustomerPhoto() {
      const cd = this.customizationData || {};
      return !!((Array.isArray(cd.originalImages) && cd.originalImages.length) || cd.originalImage);
    },
    /** Bereiche lassen sich markieren, sobald ein Bild da ist (mit oder ohne Upscale-Job) */
    regionEditAvailable() {
      const cd = this.customizationData || {};
      return !!(cd.upscaledImage || cd.artworkImageUrl) && !!cd.artworkImageUrl;
    },
    /** Kleine Vorschau des markierten Bereichs: Upscale als Hintergrund, auf die Box gezoomt */
    customRegionThumbStyle() {
      return this.boxThumbStyle(this.customRegion && this.customRegion.box);
    },
    /** Laufende Bereichs-Edits als Kacheln („In Bearbeitung" in der KI-Box) — Ausschnitt aus dem aktuellen Bild */
    runningEditTiles() {
      return this.regionEditsActive.map((s) => {
        const known = s.regionId ? (this.upscaleRegions || []).find((r) => r.id === s.regionId) : null;
        const box = s.box || (known && known.box) || null;
        return {
          key: s.jobId || s.regionId || String(s.at),
          label: s.label || (known && known.label) || "Bereich",
          state: s.state === "queued" ? "wartet" : "wird gerendert …",
          thumbStyle: this.boxThumbStyle(box),
        };
      });
    },
    /** Rahmen des markierten, noch nicht gestarteten Bereichs (Prozent) — bleibt sichtbar, solange das Formular offen ist */
    customRegionBoxStyle() {
      const b = this.customRegion && this.customRegion.box;
      if (!b) return null;
      return { left: b.x / 10 + "%", top: b.y / 10 + "%", width: b.width / 10 + "%", height: b.height / 10 + "%" };
    },
    /** Rahmen aller Bereiche, die gerade gerendert werden (Prozent) — pulsieren im Bild */
    regionEditRunningBoxes() {
      return this.regionEditsActive
        .map((s) => {
          const known = s.regionId ? (this.upscaleRegions || []).find((r) => r.id === s.regionId) : null;
          const b = s.box || (known && known.box);
          if (!b) return null;
          return { key: s.jobId || s.regionId || "run", style: { left: b.x / 10 + "%", top: b.y / 10 + "%", width: b.width / 10 + "%", height: b.height / 10 + "%" } };
        })
        .filter(Boolean);
    },
    /** Kompatibilität: erster laufender Rahmen */
    regionEditRunningBox() {
      const first = this.regionEditRunningBoxes[0];
      return first ? first.style : null;
    },
    /** Rahmen der laufenden Auswahl (Prozent) */
    regionDrawStyle() {
      const d = this.regionDraw;
      if (!d) return null;
      const x = Math.min(d.x1, d.x2), y = Math.min(d.y1, d.y2);
      return { left: x / 10 + "%", top: y / 10 + "%", width: Math.abs(d.x2 - d.x1) / 10 + "%", height: Math.abs(d.y2 - d.y1) / 10 + "%" };
    },
  },
  watch: {
    // Einordnung „nicht zuordenbar" → den eigenen Wunsch merken, damit der Voll-Edit als Rückfall angeboten wird
    feedbackEditState(s) {
      if (s && s.state === "noAction" && this.smartEditPrompt) this.smartEditUnclear = { prompt: this.smartEditPrompt, reason: s.reason || "" };
      if (s && s.state === "done") {
        this.smartEditPrompt = "";
        // Neue Version „Edit N" direkt auswählen, sobald sie am Task angekommen ist
        if (s.aiEditUrl && Array.isArray(this.aiEdits)) {
          const pick = () => {
            const i = this.aiEdits.findIndex((e) => e && e.url === s.aiEditUrl);
            if (i >= 0) this.selectedVariant = "AI_EDIT_" + i;
            return i >= 0;
          };
          if (!pick()) setTimeout(pick, 1500);
        }
      }
    },
    // Vergleichsdialog zu → das Formular beim nächsten Öffnen nicht automatisch aufklappen
    compareDialog(open) {
      if (!open) this.regionEditFocusId = null;
    },
  },
  methods: {
    /** Edit starten: region = { source: 'existing'|'more'|'custom', id?, type?, label?, boxResult? } */
    /**
     * Region-Edit starten. `inputs` = was neben dem Ausschnitt ans Modell geht ({ photo, artwork });
     * ein String "photo"/"artwork" (alte Aufrufer) wird übersetzt.
     */
    async startRegionEdit(region, instruction = "", inputs = { photo: true, artwork: true }) {
      if (typeof inputs === "string") inputs = { photo: inputs !== "artwork", artwork: true };
      if (!this.hasCustomerPhoto) inputs = { ...inputs, photo: false };
      if (!inputs.current && !inputs.upscale) inputs = { ...inputs, current: true };
      const referenceMode = inputs.photo ? "photo" : "artwork";
      // Mehrere Bereiche dürfen parallel laufen — nur derselbe Bereich nicht doppelt (der Server lehnt das sonst ab)
      if (this.regionEditBusy) return;
      if (region && region.id && this.isRegionEditRunning(region.id)) return;
      if (!this.upscaleJob && !(this.task && this.task.uid)) return;
      this.regionEditBusy = true;
      try {
        const call = httpsCallable(regionalFunctions, "regionEditCall", { timeout: 120000 });
        // Ohne Upscale-Job (alter Upscaler): der Server legt für den Task einen Stellvertreter-Job an
        await call({ ...(this.upscaleJob ? { parentJobId: this.upscaleJob.id } : { taskUid: this.task.uid }), region, instruction, referenceMode, inputs });
        this.customRegion = null;
        this.regionDrawMode = false;
      } catch (err) {
        console.error("Region-Edit:", err);
        EventBus.$emit(events.SNACKBAR, { message: `Region-Edit konnte nicht gestartet werden: ${(err && err.message) || err}`, type: "error" });
      } finally {
        this.regionEditBusy = false;
      }
    },
    /** CSS-Hintergrund: aktuelles Bild, auf eine Box (Promille) gezoomt */
    boxThumbStyle(b) {
      const cd = this.customizationData || {};
      const url = cd.upscaledImage || cd.artworkImageUrl;
      if (!b || !url || !b.width || !b.height) return null;
      const px = b.width >= 1000 ? 0 : (b.x / (1000 - b.width)) * 100;
      const py = b.height >= 1000 ? 0 : (b.y / (1000 - b.height)) * 100;
      return { backgroundImage: `url(${url})`, backgroundSize: `${(1000 / b.width) * 100}% ${(1000 / b.height) * 100}%`, backgroundPosition: `${px}% ${py}%` };
    },
    /** Läuft für diesen Bereich gerade ein Edit? */
    isRegionEditRunning(regionId) {
      return !!regionId && this.regionEditRunningIds.includes(regionId);
    },
    /** Große Ansicht (Vergleich) mit offenem Formular für diese Region */
    openRegionEditLarge(id) {
      this.regionEditFocusId = id;
      this.openUpscaleFace(id);
    },
    /** Gespeicherte Version eines Bereichs aktivieren (Ausschnitt wird ins Endbild eingesetzt, kein Modellaufruf) */
    async activateRegionVersion(regionId, versionIndex) {
      if (!this.upscaleJob || this.regionEditBusy || this.regionEditRunning) return;
      this.regionEditBusy = true;
      try {
        const call = httpsCallable(regionalFunctions, "regionEditCall", { timeout: 120000 });
        await call({ action: "version", parentJobId: this.upscaleJob.id, regionId, versionIndex });
      } catch (err) {
        EventBus.$emit(events.SNACKBAR, { message: `Version konnte nicht aktiviert werden: ${(err && err.message) || err}`, type: "error" });
      } finally {
        this.regionEditBusy = false;
      }
    },
    /** Letzten Edit zurücknehmen (Endbild, Report und Task auf den Stand davor) */
    async revertRegionEdit(editIndex) {
      if (!this.upscaleJob || this.regionEditBusy) return;
      this.regionEditBusy = true;
      try {
        const call = httpsCallable(regionalFunctions, "regionEditCall", { timeout: 60000 });
        await call({ action: "revert", parentJobId: this.upscaleJob.id, editIndex });
      } catch (err) {
        EventBus.$emit(events.SNACKBAR, { message: `Zurücknehmen fehlgeschlagen: ${(err && err.message) || err}`, type: "error" });
      } finally {
        this.regionEditBusy = false;
      }
    },

    /**
     * Freier Änderungswunsch ohne markierten Bereich: dieselbe Einordnung wie der Auto-Edit aus AllRatings.
     * Farbe/Ton per Kurve, ein Bereich per Region-Edit, Detail außerhalb mit Maske + Regionen wieder drauf.
     * „Nicht zuordenbar" generiert nichts — dann bietet die Box den alten Voll-Edit an.
     */
    async startSmartEdit(comment) {
      const text = String(comment || "").trim();
      if (!text || this.smartEditBusy || this.feedbackEditRunning || this.regionEditRunning) return;
      if (!this.upscaleJob && !(this.task && this.task.uid)) return;
      this.smartEditBusy = true;
      this.smartEditUnclear = null;
      this.smartEditPrompt = text;
      try {
        const call = httpsCallable(regionalFunctions, "feedbackEditCall", { timeout: 120000 });
        // Grundlage = die im Editor AUSGEWÄHLTE Version (wie beim manuellen Voll-Edit); Ergebnis wird „Edit N"
        const baseUrl = (typeof this.currentImageUrl === "string" && this.currentImageUrl) || null;
        await call({ ...(this.upscaleJob ? { parentJobId: this.upscaleJob.id } : { taskUid: this.task.uid }), comment: text, ...(baseUrl ? { baseUrl } : {}), asEdit: true });
      } catch (err) {
        console.error("Feedback-Edit:", err);
        EventBus.$emit(events.SNACKBAR, { message: `KI-Änderung konnte nicht gestartet werden: ${(err && err.message) || err}`, type: "error" });
      } finally {
        this.smartEditBusy = false;
      }
    },

    // ── Rechteck auf dem Artwork ziehen ──
    toggleRegionDraw() {
      this.regionDrawMode = !this.regionDrawMode;
      this.regionDraw = null;
      if (!this.regionDrawMode) this.customRegion = null;
    },
    /** Mausposition → Promille des Artworks (Bounding-Box des Overlays; bei Drehung ungenau, dann gesperrt) */
    regionDrawPoint(e) {
      const el = this.$refs.regionDrawLayer;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      return { x: Math.max(0, Math.min(1000, ((e.clientX - r.left) / r.width) * 1000)), y: Math.max(0, Math.min(1000, ((e.clientY - r.top) / r.height) * 1000)) };
    },
    onRegionDrawDown(e) {
      if (!this.regionDrawMode) return;
      const p = this.regionDrawPoint(e);
      if (!p) return;
      e.preventDefault();
      e.stopPropagation();
      this.regionDraw = { x1: p.x, y1: p.y, x2: p.x, y2: p.y, active: true };
    },
    onRegionDrawMove(e) {
      if (!this.regionDraw || !this.regionDraw.active) return;
      const p = this.regionDrawPoint(e);
      if (!p) return;
      this.regionDraw = { ...this.regionDraw, x2: p.x, y2: p.y };
    },
    onRegionDrawUp() {
      const d = this.regionDraw;
      if (!d || !d.active) return;
      const box = { x: Math.round(Math.min(d.x1, d.x2)), y: Math.round(Math.min(d.y1, d.y2)), width: Math.round(Math.abs(d.x2 - d.x1)), height: Math.round(Math.abs(d.y2 - d.y1)) };
      this.regionDraw = { ...d, active: false };
      if (box.width < 20 || box.height < 20) {
        this.regionDraw = null;
        return;
      }
      this.customRegion = { box, type: "face", label: "", instruction: "", inputs: { current: true, upscale: false, photo: this.hasCustomerPhoto, artwork: true } };
      this.regionDrawMode = false; // Auswahl steht — Overlay weg, Bubbles wieder da, Formular in der KI-Box
    },
    submitCustomRegion() {
      const c = this.customRegion;
      if (!c) return;
      const typeLabel = (REGION_TYPES.find((t) => t.value === c.type) || {}).label || c.type;
      this.startRegionEdit({ source: "custom", type: c.type, label: c.label.trim() || typeLabel, boxResult: c.box }, c.instruction, c.inputs);
    },
    /** Grundlage des markierten Bereichs umschalten — Überarbeitung oder Upscale bleibt immer gewählt */
    toggleCustomBase(key) {
      const c = this.customRegion;
      if (!c) return;
      const other = key === "current" ? "upscale" : "current";
      if (c.inputs[key] && !c.inputs[other]) return;
      c.inputs[key] = !c.inputs[key];
    },
    cancelCustomRegion() {
      this.customRegion = null;
      this.regionDraw = null;
      this.regionDrawMode = false;
    },
  },
};
